import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, LogOut } from "lucide-react";
import { BookingsTable } from "@/components/admin/BookingsTable";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { SheetPanel } from "@/components/admin/SheetPanel";
import { ContentPanel } from "@/components/admin/ContentPanel";
import { PrPanel } from "@/components/admin/PrPanel";
import { API, authHeaders, fetchConfig, SITE } from "@/lib/api";

export default function Admin() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [me, setMe] = useState(null);
  const [filter, setFilter] = useState("all");
  const [bookings, setBookings] = useState({ items: [], eventDates: [] });

  const logout = useCallback(async () => {
    localStorage.removeItem("eclipsia_admin_token");
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin/login");
  }, [navigate]);

  const loadBookings = useCallback(async () => {
    try {
      const params = filter !== "all" ? { eventDate: filter } : {};
      const { data } = await axios.get(`${API}/admin/bookings`, { params, headers: authHeaders() });
      setBookings(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  }, [filter, logout]);

  useEffect(() => {
    if (!localStorage.getItem("eclipsia_admin_token")) return navigate("/admin/login");
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setMe(data))
      .catch(() => logout());
    fetchConfig().then(setConfig);
  }, [navigate, logout]);

  const isAdmin = me?.role === "admin";

  useEffect(() => { loadBookings(); }, [loadBookings]);

  return (
    <main className="min-h-screen bg-black px-4 md:px-8 py-8" data-testid="admin-dashboard">
      <header className="max-w-7xl mx-auto flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <img src={SITE.logo} alt="Eclipsia" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline text-xs tracking-[0.3em] uppercase text-white/50 font-medium" data-testid="admin-role-label">
            {me?.role === "pr" ? `${me.name} · ${me.lista}` : "Area Riservata"}
          </span>
        </div>
        <div className="flex gap-2">
          <Link to="/" target="_blank" data-testid="admin-view-site" className="glass rounded-full px-4 py-2 text-sm text-white/80 hover:text-white flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">Sito</span>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="glass rounded-full px-4 py-2 text-sm text-white/80 hover:text-white flex items-center gap-2">
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Esci</span>
          </button>
        </div>
      </header>
      {me && !isAdmin && (
        <div className="max-w-7xl mx-auto">
          <BookingsTable data={bookings} filter={filter} onFilter={setFilter} onChanged={loadBookings} readOnly title={me.lista} />
        </div>
      )}
      {isAdmin && (
        <>
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[360px_1fr] gap-6 items-start">
            <div className="flex flex-col gap-6 min-w-0">
              {config && <SettingsPanel config={config} onSaved={(c) => { setConfig(c); loadBookings(); }} />}
              <SheetPanel />
            </div>
            <BookingsTable data={bookings} filter={filter} onFilter={setFilter} onChanged={loadBookings} />
          </div>
          <div className="max-w-7xl mx-auto mt-6">
            {config && <PrPanel liste={config.liste} />}
          </div>
          <div className="max-w-7xl mx-auto mt-6">
            {config && <ContentPanel config={config} onSaved={setConfig} />}
          </div>
        </>
      )}
    </main>
  );
}
